import { useId, useState } from "react";
import { REASON_MAX_LENGTH, reasonProblem } from "../_helpers/reason";

interface ReasonFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    disabled: boolean;
    required?: boolean;
    // The line under the field when nothing is wrong.
    hint?: string;
    // The line while a required reason is empty. A confirm that a missing
    // reason disables points its aria-describedby at `messageId`.
    missingMessage?: string;
    // The id of that line, so a control outside can be described by it.
    messageId?: string;
    placeholder?: string;
}

// The admin reason textarea (review finding 10): a label, the trimmed-length
// counter against the API's cap, and one line under it that says what is
// wrong, or the hint. No maxLength: a pasted reason cut short without a word
// would be sent as something the admin never wrote, so an over-long one is
// refused visibly instead. The caller decides what the problem blocks.
export const ReasonField = ({
    label,
    value,
    onChange,
    disabled,
    required = false,
    hint,
    missingMessage,
    messageId,
    placeholder,
}: ReasonFieldProps) => {
    const fieldId = useId();
    const fallbackMessageId = useId();
    const lineId = messageId ?? fallbackMessageId;
    // An empty required reason is only called invalid once the admin has
    // been in the field: on open it is unanswered, not wrong.
    const [isTouched, setIsTouched] = useState(false);

    const problem = reasonProblem(value, required);
    const isInvalid =
        problem === "too-long" || (problem === "missing" && isTouched);
    const line =
        problem === "too-long"
            ? `Keep the reason to ${REASON_MAX_LENGTH} characters or fewer.`
            : problem === "missing"
                ? missingMessage
                : hint;

    return (
        <div className="mb-4">
            <label htmlFor={fieldId} className="mb-1 block text-sm font-semibold">
                {label}
            </label>
            <textarea
                id={fieldId}
                className="textarea textarea-bordered w-full"
                rows={3}
                value={value}
                disabled={disabled}
                required={required}
                aria-required={required || undefined}
                aria-invalid={isInvalid}
                aria-describedby={lineId}
                placeholder={placeholder}
                onChange={(event) => {
                    setIsTouched(true);
                    onChange(event.target.value);
                }}
                onBlur={() => setIsTouched(true)}
            />
            <div className="mt-1 flex justify-between gap-2 text-xs">
                <p
                    id={lineId}
                    className={isInvalid ? "text-error" : "text-neutral/70"}
                >
                    {line}
                </p>
                <span className="shrink-0 text-neutral/70">
                    {`${value.trim().length}/${REASON_MAX_LENGTH}`}
                </span>
            </div>
        </div>
    );
};
